import { useEffect, useMemo, useState } from "react"
import { DailyVideo, useLocalSessionId, useParticipantIds, useScreenShare } from "@daily-co/daily-react"
import { fitGridTiles } from "./Stage"
import { useActiveSpeakerHysteresis } from "./useActiveSpeakerHysteresis"
import { VideoTile } from "./VideoTile"
import { useScreenSharerLabel } from "./useScreenSharerLabel"

/** Most participant tiles the floating window shows before folding the rest into a "+N" tile. */
export const PIP_MAX_TILES = 9
const PIP_MAX_COLUMNS = 3
const PIP_GAP = 6

type Size = { width: number; height: number }

/**
 * Content-box size of an element inside the picture-in-picture document. The observer comes
 * from that document's own window, since the element does not belong to the opener.
 */
const useElementSize = (element: HTMLElement | null): Size | null => {
  const [size, setSize] = useState<Size | null>(null)
  useEffect(() => {
    if (!element) return
    const view = element.ownerDocument.defaultView as (Window & typeof globalThis) | null
    const Observer = view?.ResizeObserver || (typeof ResizeObserver === "function" ? ResizeObserver : null)
    if (!Observer) {
      setSize({ width: element.clientWidth, height: element.clientHeight })
      return
    }
    const observer = new Observer((entries) => {
      const box = entries[0]?.contentBoxSize?.[0]
      if (box) setSize({ width: box.inlineSize, height: box.blockSize })
      else setSize({ width: element.clientWidth, height: element.clientHeight })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])
  return size
}

/** Picks the tiles to show: joined order, always including the active speaker, capped at `max`. */
export const pickPipTileIds = (ids: string[], activeSpeakerId: string | null, max: number) => {
  if (ids.length <= max) return { visible: ids, hidden: 0 }
  const keep = max - 1
  const visible = ids.slice(0, keep)
  if (activeSpeakerId && ids.includes(activeSpeakerId) && !visible.includes(activeSpeakerId)) {
    visible[keep - 1] = activeSpeakerId
  }
  return { visible, hidden: ids.length - visible.length }
}

function PipTile({ sessionId, isLocal, isActiveSpeaker, variant }: { sessionId: string; isLocal: boolean; isActiveSpeaker: boolean; variant: "grid" | "thumb" }) {
  // The tile itself shows the microphone badge and the speaking outline; Daily's active speaker backs up its level observer.
  return <VideoTile sessionId={sessionId} variant={variant} isLocal={isLocal} isActiveSpeaker={isActiveSpeaker} />
}

/**
 * The picture-in-picture window's view of the call, laid out like Google Meet's: every
 * participant in a grid that re-fits itself to the window size (16:9 tiles, up to three
 * columns), the active speaker outlined, and a microphone badge on each tile that shows muted,
 * quiet, or speaking. A remote screen share takes the top with the people in a strip beneath it.
 */
export function PictureInPictureGrid() {
  const localSessionId = useLocalSessionId()
  const participantIds = useParticipantIds({ sort: "joined_at" })
  const { screens } = useScreenShare()
  const activeSpeakerId = useActiveSpeakerHysteresis()
  const screenShare = useMemo(() => screens.find((screen) => !screen.local) || null, [screens])
  const screenShareLabel = useScreenSharerLabel(screenShare?.session_id || "", false)
  const { visible, hidden } = useMemo(() => pickPipTileIds(participantIds, activeSpeakerId, PIP_MAX_TILES), [activeSpeakerId, participantIds])
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null)
  const size = useElementSize(gridElement)
  const tileCount = visible.length + (hidden > 0 ? 1 : 0)
  const fit = size ? fitGridTiles(tileCount, size.width, size.height, PIP_MAX_COLUMNS, PIP_GAP) : null
  const columns = fit?.columns ?? Math.min(PIP_MAX_COLUMNS, Math.max(1, Math.ceil(Math.sqrt(tileCount))))

  if (screenShare) {
    return (
      <div data-testid="call-pip-grid" data-pip-layout="screen-share" className="flex h-full w-full flex-col gap-1.5">
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-black ring-1 ring-white/10">
          <DailyVideo sessionId={screenShare.session_id} type="screenVideo" fit="contain" playsInline className="h-full w-full" />
          <span data-testid="call-pip-screen-share-label" className="absolute left-1.5 top-1.5 max-w-[calc(100%-0.75rem)] truncate rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">{screenShareLabel}</span>
        </div>
        <div className="flex h-16 shrink-0 gap-1.5 overflow-x-auto">
          {visible.map((id) => (
            <div key={id} className="h-full w-[7.1rem] shrink-0">
              <PipTile sessionId={id} isLocal={id === localSessionId} isActiveSpeaker={id === activeSpeakerId} variant="thumb" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={setGridElement}
      data-testid="call-pip-grid"
      data-pip-layout="grid"
      data-pip-columns={columns}
      className="grid h-full w-full content-center justify-center"
      style={{ gap: PIP_GAP, gridTemplateColumns: fit ? `repeat(${columns}, ${fit.tileWidth}px)` : `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {visible.map((id) => (
        <PipTile key={id} sessionId={id} isLocal={id === localSessionId} isActiveSpeaker={id === activeSpeakerId} variant="grid" />
      ))}
      {hidden > 0 && (
        <div data-testid="call-pip-hidden" className="flex aspect-video items-center justify-center rounded-2xl bg-[#1c2530] text-sm font-medium text-white/70 ring-1 ring-white/5">
          +{hidden} more
        </div>
      )}
    </div>
  )
}
